// Client-side mirror of the backend's per-route rate limit (1 request per route
// per second). The backend buckets by *path* (ignoring query params), so two
// concurrent calls to the same path with different params race and one gets 429.
// This gate serialises same-route requests so they are dispatched at least
// ROUTE_MIN_INTERVAL_MS apart, drops requests aborted while still queued (so
// they never burn the route's budget), and retries a 429 once — through the same
// queue, so the retry is spaced too and never collides with a pending request.
//
// Disabled only under Vitest (see computeRouteRateLimitEnabled). The single
// on/off switch lives here.

export const ROUTE_MIN_INTERVAL_MS = 1100;
export const ROUTE_MAX_RETRIES = 1;
// Cap on a server-provided Retry-After so a stray/huge value cannot overflow
// setTimeout's 32-bit delay (which would fire immediately) or stall a request
// for hours.
export const ROUTE_MAX_BACKOFF_MS = 30000;

/**
 * Whether the gate is active. Active in all builds except Vitest (`MODE ===
 * "test"`), where artificial 1s spacing would stall the suite. Params are a test
 * seam, mirroring `combineWithTimeout(signal, abortDisabled)` in abort.ts.
 */
export function computeRouteRateLimitEnabled(
  _dev: boolean = import.meta.env.DEV === true,
  mode: string = import.meta.env.MODE,
): boolean {
  return mode !== "test";
}

let enabled = computeRouteRateLimitEnabled();

export function isRouteRateLimitEnabled(): boolean {
  return enabled;
}

/** Single switch to flip the gate on/off (runtime/test seam). */
export function setRouteRateLimitEnabled(value: boolean): void {
  enabled = value;
}

type RouteSlot = {
  // Resolves once the previous queued request for this route reserved its slot.
  tail: Promise<void>;
  // Spacing anchor: timestamp of the last *dispatched* send, possibly pushed
  // forward by a Retry-After backoff. NEGATIVE_INFINITY = never sent.
  lastSentAt: number;
};

const slots = new Map<string, RouteSlot>();

/** Test helper: clears the per-route queue state between cases. */
export function __resetRouteRateLimiter(): void {
  slots.clear();
}

function abortError(): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException("The user aborted a request.", "AbortError");
  }
  const e = new Error("The user aborted a request.");
  e.name = "AbortError";
  return e;
}

function sleepAbortable(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    let onAbort: (() => void) | undefined;
    const id = setTimeout(() => {
      if (signal && onAbort) signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    if (signal) {
      onAbort = () => {
        clearTimeout(id);
        reject(abortError());
      };
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}

/** Retry-After in ms (seconds or HTTP-date form), clamped; undefined if absent. */
function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  let ms: number;
  if (Number.isFinite(seconds)) {
    ms = seconds * 1000;
  } else {
    const date = Date.parse(header);
    if (!Number.isFinite(date)) return undefined;
    ms = date - Date.now();
  }
  if (ms <= 0) return undefined;
  return Math.min(ms, ROUTE_MAX_BACKOFF_MS);
}

/**
 * Waits until this request may be dispatched: behind any pending request for the
 * route AND at least ROUTE_MIN_INTERVAL_MS after the route's spacing anchor. A
 * request aborted (via `signal`) while waiting rejects without advancing the
 * anchor, so it never consumes the route's budget. Resolving updates the anchor;
 * the returned promise is also chained onto `slot.tail` so the next reservation
 * (the next request, or this one's retry) starts only once this one settles.
 */
function reserveSlot(
  slot: RouteSlot,
  signal: AbortSignal | undefined,
): Promise<void> {
  const reserved = slot.tail.then(async () => {
    const wait = Math.max(0, slot.lastSentAt + ROUTE_MIN_INTERVAL_MS - Date.now());
    // Always go through sleepAbortable so an already-aborted signal rejects here
    // (even when no spacing is due) without advancing the anchor below.
    await sleepAbortable(wait, signal);
    slot.lastSentAt = Date.now();
  });
  // Swallow errors on the queue chain so an aborted/failed reservation never
  // stalls the rest of the route's queue.
  slot.tail = reserved.catch(() => undefined);
  return reserved;
}

async function dispatchWithRetry(
  slot: RouteSlot,
  send: () => Promise<Response>,
  signal: AbortSignal | undefined,
): Promise<Response> {
  let attempt = 0;
  for (;;) {
    await reserveSlot(slot, signal);
    const resp = await send();
    if (resp.status !== 429 || attempt >= ROUTE_MAX_RETRIES) return resp;
    attempt += 1;
    const backoff = parseRetryAfter(resp.headers.get("Retry-After"));
    if (backoff !== undefined) {
      // Push the route's spacing anchor forward so the retry — and any other
      // request queued behind it — waits at least the server-requested delay.
      slot.lastSentAt = Math.max(
        slot.lastSentAt,
        Date.now() + backoff - ROUTE_MIN_INTERVAL_MS,
      );
    }
    // No backoff header → the next reserveSlot still waits ROUTE_MIN_INTERVAL_MS
    // from this send, which already covers the backend's 1s window.
  }
}

/**
 * Queues `send` behind any pending request for `key`, spacing dispatches by at
 * least ROUTE_MIN_INTERVAL_MS and retrying a 429 once through the same queue.
 */
export function scheduleRouteRequest(
  key: string,
  init: RequestInit | undefined,
  send: () => Promise<Response>,
): Promise<Response> {
  const signal = init?.signal ?? undefined;
  let slot = slots.get(key);
  if (!slot) {
    slot = { tail: Promise.resolve(), lastSentAt: Number.NEGATIVE_INFINITY };
    slots.set(key, slot);
  }
  return dispatchWithRetry(slot, send, signal);
}

/** Route key the backend rate-limiter buckets by: `METHOD pathname` (no query). */
export function routeKeyOf(
  input: RequestInfo | URL,
  init?: RequestInit,
): string {
  const method = (
    init?.method ?? (input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  const rawUrl =
    input instanceof Request
      ? input.url
      : input instanceof URL
        ? input.href
        : String(input);
  const base = globalThis.location?.origin ?? "http://localhost";
  let pathname: string;
  try {
    pathname = new URL(rawUrl, base).pathname;
  } catch {
    pathname = rawUrl.split("?")[0];
  }
  return `${method} ${pathname}`;
}

/**
 * Entry point for the api.ts `fetch` wrapper. Passes through untouched when the
 * gate is disabled or for keepalive requests (the meta-PUT unload flush, which
 * must never be queued); otherwise routes through the per-route scheduler.
 */
export function gatedFetch(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  send: () => Promise<Response>,
): Promise<Response> {
  if (!enabled || init?.keepalive === true) return send();
  return scheduleRouteRequest(routeKeyOf(input, init), init, send);
}
